/* global DeferredMedia */
/* DWC media repair 6: load the GLB, activate the viewer and dismiss its poster. */
(() => {
  const withTimeout = (promise, delay, message) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), delay);
    Promise.resolve(promise).then((value) => {
      clearTimeout(timer);
      resolve(value);
    }, (error) => { clearTimeout(timer); reject(error); });
  });

  function loadViewerUI() {
    if (typeof window.Shopify?.ModelViewerUI === 'function') return Promise.resolve();
    if (!window.dwcModelViewerUIPromise) {
      const request = new Promise((resolve, reject) => {
        const started = Date.now();
        const start = () => {
          if (typeof window.Shopify?.loadFeatures !== 'function') {
            if (Date.now() - started > 6000) reject(new Error('Shopify model viewer loader is unavailable.'));
            else setTimeout(start, 100);
            return;
          }
          window.Shopify.loadFeatures([{
            name: 'model-viewer-ui', version: '1.0',
            onLoad(errors) {
              if (errors && (!Array.isArray(errors) || errors.length)) {
                reject(new Error('The Shopify model viewer library failed to load.'));
              } else if (typeof window.Shopify.ModelViewerUI !== 'function') {
                reject(new Error('Shopify ModelViewerUI did not initialise.'));
              } else resolve();
            }
          }]);
        };
        start();
      });
      window.dwcModelViewerUIPromise = withTimeout(request, 15000, 'The model viewer library timed out.');
      window.dwcModelViewerUIPromise.catch(() => { window.dwcModelViewerUIPromise = null; });
    }
    return window.dwcModelViewerUIPromise;
  }

  if (!customElements.get('product-model')) {
    class ProductModel extends DeferredMedia {
      constructor() {
        super();
        this.addEventListener('mousemove', (event) => {
          if (this.modelViewer && !this.modelViewer.classList.contains('shopify-model-viewer-ui__disabled')) {
            event.stopPropagation();
          }
        });
      }

      /** Returns a promise. Setting [loaded] alone does NOT mean the GLB is displayed. */
      loadContent(focus = false, pause = true, loadTrigger = 'click') {
        if (pause && typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        if (!this.dwcLoadPromise) this.dwcLoadPromise = this.loadAndReveal(focus === true, loadTrigger);
        const result = this.dwcLoadPromise.then((viewer) => {
          this.activateModel();
          return viewer;
        });
        // Native poster event handlers ignore return values. Attach a handler without
        // swallowing the rejection returned to the gallery's loading/error UI.
        result.catch((error) => {
          this.dwcLoadPromise = null;
          this.dataset.dwcModelState = 'error';
          this.dispatchEvent(new CustomEvent('dwc:model-error', { bubbles: true, detail: { error } }));
        });
        return result;
      }

      async loadAndReveal(focus, loadTrigger) {
        this.dataset.dwcModelState = 'loading';
        // Keep the theme's original order: insert model markup before loading its UI.
        super.loadContent(false, false, loadTrigger);
        this.modelViewer = this.querySelector('model-viewer');
        const viewer = this.modelViewer;
        if (!viewer?.getAttribute('src')) throw new Error('The model has no GLB source URL.');
        viewer.setAttribute('loading', 'eager');
        viewer.setAttribute('reveal', 'auto');
        viewer.setAttribute('camera-controls', '');

        let cleanup = () => {};
        const rendered = new Promise((resolve, reject) => {
          const onLoad = () => { cleanup(); resolve(); };
          const onError = () => { cleanup(); reject(new Error('The GLB could not load, or WebGL is unavailable.')); };
          const timer = setTimeout(() => {
            cleanup();
            reject(new Error('The GLB did not finish loading within 30 seconds.'));
          }, 30000);
          cleanup = () => {
            clearTimeout(timer);
            viewer.removeEventListener('load', onLoad);
            viewer.removeEventListener('error', onError);
          };
          viewer.addEventListener('load', onLoad);
          viewer.addEventListener('error', onError);
          if (viewer.loaded === true) onLoad();
        });
        rendered.catch(() => {});

        try {
          await loadViewerUI();
          if (!this.modelViewerUI) this.modelViewerUI = new window.Shopify.ModelViewerUI(viewer);
          this.activateModel();
          await withTimeout(customElements.whenDefined('model-viewer'), 15000, 'The 3D renderer did not initialise.');
          this.activateModel();
          await rendered;
          this.activateModel();
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          if (viewer.loaded !== true) throw new Error('The renderer has not confirmed that the model is loaded.');
          this.dataset.dwcModelState = 'ready';
          if (focus) viewer.focus();
          this.dispatchEvent(new CustomEvent('dwc:model-ready', { bubbles: true, detail: { viewer } }));
          return viewer;
        } catch (error) {
          cleanup();
          throw error;
        }
      }

      activateModel() {
        const viewer = this.modelViewer;
        if (!viewer) return;
        viewer.setAttribute('loading', 'eager');
        viewer.setAttribute('reveal', 'auto');
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('touch-action', 'pan-y');
        viewer.removeAttribute('toggleable');
        if (typeof this.modelViewerUI?.play === 'function') this.modelViewerUI.play();
        if (typeof viewer.dismissPoster === 'function') viewer.dismissPoster();
      }
    }
    customElements.define('product-model', ProductModel);
  }

  // Retain the theme's existing augmented reality setup.
  window.ProductModel = {
    loadShopifyXR() {
      if (typeof window.Shopify?.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures([{
        name: 'shopify-xr', version: '1.0', onLoad: this.setupShopifyXR.bind(this)
      }]);
    },
    setupShopifyXR(errors) {
      if (errors && (!Array.isArray(errors) || errors.length)) return;
      if (!window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR(), { once: true });
        return;
      }
      document.querySelectorAll('[id^="product-json-"]').forEach((element) => {
        try { window.ShopifyXR.addModels(JSON.parse(element.textContent)); element.remove(); }
        catch (error) { console.warn('[DWC 3D] Invalid AR model data.', error); }
      });
      window.ShopifyXR.setupXRElements();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.ProductModel.loadShopifyXR(), { once: true });
  } else window.ProductModel.loadShopifyXR();
})();